import React, { useEffect, useRef, useState } from 'react';
import { AlertTriangle, Check, Info, X } from 'lucide-react';
import type { ToastItem, ToastType } from './types';

type ToastViewportProps = {
  toasts: ToastItem[];
  onDismiss: (id: string) => void;
  onPause: (id: string, paused: boolean) => void;
};

const TYPE_STYLES: Record<
  ToastType,
  {
    card: string;
    iconWrap: string;
    iconColor: string;
  }
> = {
  success: {
    card: 'bg-[#FFF9F5] border-[#FFE0CC]',
    iconWrap: 'bg-[#FFE8D6]',
    iconColor: 'text-[#FF7A21]',
  },
  warning: {
    card: 'bg-[#FFFBF2] border-[#FFE6B8]',
    iconWrap: 'bg-[#FFE8C2]',
    iconColor: 'text-[#D97706]',
  },
  error: {
    card: 'bg-[#FFF7F7] border-[#F5D0D0]',
    iconWrap: 'bg-[#FDE2E2]',
    iconColor: 'text-[#C62828]',
  },
  info: {
    card: 'bg-[#FBF8FC] border-[#E8DCE8]',
    iconWrap: 'bg-[#F3E6F0]',
    iconColor: 'text-[#660055]',
  },
};

function ToastIcon({ type }: { type: ToastType }) {
  const className = 'h-4 w-4';
  switch (type) {
    case 'success':
      return <Check className={className} strokeWidth={2.6} />;
    case 'warning':
      return <AlertTriangle className={className} strokeWidth={2.4} />;
    case 'error':
      return <X className={className} strokeWidth={2.6} />;
    default:
      return <Info className={className} strokeWidth={2.4} />;
  }
}

function ToastCard({
  toast,
  onDismiss,
  onPause,
}: {
  toast: ToastItem;
  onDismiss: (id: string) => void;
  onPause: (id: string, paused: boolean) => void;
}) {
  const [entering, setEntering] = useState(true);
  const [leaving, setLeaving] = useState(false);
  const leaveTimer = useRef<number | null>(null);

  useEffect(() => {
    const frame = window.requestAnimationFrame(() => setEntering(false));
    return () => window.cancelAnimationFrame(frame);
  }, []);

  useEffect(() => {
    return () => {
      if (leaveTimer.current) window.clearTimeout(leaveTimer.current);
    };
  }, []);

  const dismiss = () => {
    if (leaving) return;
    setLeaving(true);
    leaveTimer.current = window.setTimeout(() => onDismiss(toast.id), 220);
  };

  const styles = TYPE_STYLES[toast.type];

  return (
    <div
      role={toast.type === 'error' ? 'alert' : 'status'}
      aria-live={toast.type === 'error' ? 'assertive' : 'polite'}
      onMouseEnter={() => onPause(toast.id, true)}
      onMouseLeave={() => onPause(toast.id, false)}
      className={[
        'pointer-events-auto w-[min(420px,calc(100vw-32px))] rounded-[16px] border shadow-[0_10px_30px_rgba(0,0,0,0.08)]',
        'transition-all duration-200 ease-out',
        styles.card,
        entering || leaving ? 'opacity-0 -translate-x-5' : 'opacity-100 translate-x-0',
        leaving ? '-translate-x-2.5' : '',
      ].join(' ')}
    >
      <div className="flex items-start gap-3 px-3.5 py-3.5">
        <div
          className={[
            'mt-0.5 flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl',
            styles.iconWrap,
            styles.iconColor,
          ].join(' ')}
        >
          <ToastIcon type={toast.type} />
        </div>

        <div className="min-w-0 flex-1 pt-0.5">
          <p className="text-sm font-semibold leading-snug text-[#1F1F1F]">{toast.title}</p>
          {toast.message && (
            <p className="mt-1 max-h-40 overflow-y-auto text-sm leading-relaxed text-[#6B6B6B] whitespace-pre-wrap">
              {toast.message}
            </p>
          )}
        </div>

        <button
          type="button"
          onClick={dismiss}
          className="mt-0.5 rounded-lg p-1 text-[#A0A0A0] transition-colors hover:bg-black/5 hover:text-[#555] outline-none"
          aria-label="Fechar notificação"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

export default function ToastViewport({ toasts, onDismiss, onPause }: ToastViewportProps) {
  if (toasts.length === 0) return null;

  return (
    <div
      className="pointer-events-none fixed bottom-4 left-4 z-[1100] flex flex-col-reverse gap-3 sm:bottom-6 sm:left-6"
      aria-label="Notificações"
    >
      {toasts.map((toast) => (
        <ToastCard key={toast.id} toast={toast} onDismiss={onDismiss} onPause={onPause} />
      ))}
    </div>
  );
}
